import { useAuth } from "@/context/auth";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import { AppointmentStatus, ReviewModerationStatus } from "@/models/club";
import clubRequest, { silentErrors } from "@/services/axios";
import { useGetMerchantForMerchantUser } from "@/services/merchant/utilities";
import { reviewKeys, reviews } from "@/services/reviews";
import { useReviewAccess } from "@/services/reviews/useReviewAccess";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { SectionBadge } from "./sections";

const onlineSeenKey = (phone: string) => `online-bookings-seen:${phone}`;

function dayEdge(end: boolean) {
  const date = new Date();
  if (end) date.setHours(23, 59, 59, 999);
  else date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

function readOnlineSeen(phone: string) {
  try {
    const value = localStorage.getItem(onlineSeenKey(phone));
    if (value && !Number.isNaN(Date.parse(value))) return value;
  } catch {
    // Storage unavailable: fall back to today's bookings.
  }
  return dayEdge(false);
}

// Opening the online bookings list counts as seeing every booking made so far.
export function useMarkOnlineBookingsSeen() {
  const phone = useAuth().getUserByToken()?.phoneNumber ?? "";
  const client = useQueryClient();
  useEffect(() => {
    try {
      localStorage.setItem(onlineSeenKey(phone), new Date().toISOString());
    } catch {
      // Storage unavailable: the badge keeps counting today's bookings.
    }
    void client.invalidateQueries(["home-badges", "online-bookings"]);
  }, [client, phone]);
}

type Options = {
  onlineBookings: boolean;
  pendingReviews: boolean;
  // Stylists only count bookings made with them.
  merchantUserId?: number;
};

export default function useSectionBadges({ onlineBookings, pendingReviews, merchantUserId }: Options) {
  const phone = useAuth().getUserByToken()?.phoneNumber ?? "";

  // The seen time is read on every fetch, so the always-mounted bottom bar picks it up after
  // useMarkOnlineBookingsSeen invalidates this key.
  const online = useQuery({
    queryKey: ["home-badges", "online-bookings", phone, merchantUserId ?? null],
    queryFn: async () =>
      (
        await clubRequest.appointmentQueueList(
          {
            Status: AppointmentStatus.Value0,
            MerchantUserId: merchantUserId,
            CreationTimeFrom: readOnlineSeen(phone),
            CreationTimeTo: dayEdge(true),
            Count: 1,
            Skip: 0,
          },
          silentErrors,
        )
      ).data,
    select: (data) => data.count ?? 0,
    enabled: onlineBookings,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  // Shares the reviews key prefix, so moderating a review refreshes the badge.
  const reviewsPending = useQuery({
    queryKey: [...reviewKeys.all, "pending-count"],
    queryFn: () => reviews.list({ Status: ReviewModerationStatus.Pending, Count: 1, Skip: 0 }),
    select: (data) => data.counts?.pending ?? data.totalCount ?? 0,
    enabled: pendingReviews,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const badges: Partial<Record<SectionBadge, number>> = {
    onlineBookings: onlineBookings ? online.data : undefined,
    pendingReviews: pendingReviews ? reviewsPending.data : undefined,
  };
  return badges;
}

// Badges for whoever is signed in: managers see the salon's counts, stylists only their own bookings.
export function useRoleBadges() {
  const { isManager, isStylist } = useDashboardAccess();
  const { canRead } = useReviewAccess();
  const phone = useAuth().getUserByToken()?.phoneNumber ?? "";
  const memberId = useGetMerchantForMerchantUser(phone).data?.id;
  return useSectionBadges({
    onlineBookings: isManager || (isStylist && Boolean(memberId)),
    pendingReviews: canRead,
    merchantUserId: isManager ? undefined : memberId,
  });
}
